/**
 * English, mirrored key for key against `ID_STRINGS`.
 *
 * `Record<StringKey, string>` makes an untranslated key a compile error, so the
 * two languages can never drift apart silently.
 */

import type { StringKey } from "./strings.id.js";

export const EN_STRINGS: Record<StringKey, string> = {
  // ---------- shell ----------
  "app.title": "kikik.id · Mahjong",
  "app.documentTitle": "kikik.id - Mahjong for beginners",
  "app.tagline": "Mahjong for beginners, in plain language: Learn → Solo → Online",
  "app.footer.set": "136 tiles · no flowers, no jokers · no regional scoring system chosen yet",
  "app.footer.engine": "One engine for Learn, Solo and Online.",

  // ---------- language selector ----------
  "language.label": "Language",
  "language.switchTo": "Switch language to {language}",

  // ---------- modes ----------
  "mode.nav": "Game mode",
  "mode.belajar.label": "Learn",
  "mode.belajar.hint": "Tutorial + coach",
  "mode.solo.label": "Solo",
  "mode.solo.hint": "You + 3 bots",
  "mode.online.label": "Online",
  "mode.online.hint": "Authoritative server",

  // ---------- players and seats ----------
  "player.you": "You",
  "player.bot.south": "South Bot",
  "player.bot.west": "West Bot",
  "player.bot.north": "North Bot",
  "player.generic": "Player {number}",
  "seat.east": "East",
  "seat.south": "South",
  "seat.west": "West",
  "seat.north": "North",

  // ---------- tiles ----------
  "tile.suited": "{rank} {suit}",
  "suit.characters": "Characters",
  "suit.bamboo": "Bamboo",
  "suit.dots": "Dots",
  "suit.winds": "Winds",
  "suit.dragons": "Dragons",
  "tile.WIND_EAST": "East Wind",
  "tile.WIND_SOUTH": "South Wind",
  "tile.WIND_WEST": "West Wind",
  "tile.WIND_NORTH": "North Wind",
  "tile.DRAGON_RED": "Red Dragon",
  "tile.DRAGON_GREEN": "Green Dragon",
  "tile.DRAGON_WHITE": "White Dragon",
  "tile.listSeparator": ", ",
  "tile.listAnd": " and ",

  // ---------- melds and claims ----------
  "meld.chow": "Chow (a run)",
  "meld.pong": "Pong (three of a kind)",
  "meld.kong": "Kong (four of a kind)",
  "meld.pair": "Pair",
  "claim.chow": "Chow",
  "claim.pong": "Pong",
  "claim.kong": "Kong",
  "claim.ron": "Win!",
  "claim.pass": "Pass",

  // ---------- status bar ----------
  "status.turn": "Turn",
  "status.phase": "Status",
  "status.wall": "Wall left",
  "status.wallShort": "wall",
  "status.lastDiscard": "Last discard",
  "status.none": "none yet",
  "status.ended": "—",
  "status.discardBy": "{tile} by {player}",
  "phase.awaiting-draw": "waiting for a draw",
  "phase.awaiting-discard": "waiting for a discard",
  "phase.awaiting-claims": "a discard can be claimed",
  "phase.ended": "finished",

  // ---------- table controls ----------
  "table.draw": "Draw tile",
  "table.win": "Win!",
  "table.kongFromHand": "Kong from hand",
  "table.hint": "Hint",
  "table.hint.ended": "The round is over.",
  "table.hint.claimable": "There is a discard you can claim.",
  "table.hint.waitingClaims": "Waiting for the other players to decide...",
  "table.hint.waitingPlayer": "Waiting for {player}...",
  "table.hint.draw": "Draw one tile from the wall.",
  "table.hint.drawing": "Drawing a tile from the wall...",
  "table.hint.discard": "Click a tile in your hand to discard it.",

  // ---------- your hand ----------
  "hand.title": "Your hand ({count} tiles)",
  "hand.ready": "Ready to win! Waiting for {tiles}",
  "hand.discards": "Your discards ({count})",
  "hand.noDiscards": "No discards yet.",
  "hand.discardTile": "Discard {tile}",
  "hand.arrange": "Arrange rack",
  "hand.autoSort": "Auto Sort",
  "hand.sort": "Sort",
  "hand.moveHint": "Alt + ← → to move a tile",

  // ---------- opponent seats ----------
  "seat.turnBadge": "turn",
  "seat.claimBadge": "deciding a claim",
  "seat.concealed": "{count} concealed tiles",
  "seat.discardsOf": "Discards of {player}",
  "seat.melds": "Exposed sets",

  // ---------- claim bar ----------
  "claims.title": "Claim the discard",
  "claims.waitingOthers": "Waiting for the other players to decide...",
  "claims.declared": "Already declared: {list}",
  "claims.chowHint": "({count} tiles from your hand)",
  "claims.suggested": "suggested",

  // ---------- coach ----------
  "coach.title": "Coach",
  "coach.ask": "Ask for a hint",
  "coach.follow": "Do this",
  "coach.empty": "Press “Ask for a hint” any time to have the move explained.",
  "coach.label.hint": "Hint",
  "coach.label.reason": "Reason",
  "coach.label.keep": "Keep",
  "coach.label.consider": "Consider",
  "coach.label.discard": "Discard",

  "coach.gameOver.headline": "The round is over",
  "coach.gameOver.win": "Winner: {player}.",
  "coach.gameOver.draw": "The wall ran out, so the round is a draw.",
  "coach.wait.headline": "Wait for your turn",
  "coach.wait.reason": "Another player is moving right now.",
  "coach.none.headline": "No hint yet",
  "coach.none.reason": "There is no move to suggest right now.",
  "coach.win.headline": "You can win — declare it now",
  "coach.win.reason": "Your hand is already 4 sets + 1 pair.",
  "coach.win.note": "Press the Win button to finish the round.",
  "coach.ron.headline": "Take that discard: you win!",
  "coach.ron.reason": "{tile} completes your hand into 4 sets + 1 pair.",
  "coach.ron.note": "Winning on a tile another player discarded is called a ron.",
  "coach.claim.headline": "Take the {meld}",
  "coach.claim.reason": "Taking {tile} brings your hand closer: from {before} to {after}.",
  "coach.claim.note": "Right after claiming, you discard one tile.",
  "coach.pass.headline": "Pass on this discard",
  "coach.pass.reason.noGain": "Taking this tile would not speed your hand up, and an exposed set makes your hand less flexible.",
  "coach.pass.reason.noUse": "This tile does not complete anything in your hand.",
  "coach.draw.headline": "Draw one tile from the wall",
  "coach.draw.reason": "Your turn starts by drawing a tile. Right now {steps}.",
  "coach.draw.note": "Tiles left in the wall: {count}.",
  "coach.discard.headline": "Discard {tile}",
  "coach.reason.lone-honor": "{tile} stands alone, and honour tiles cannot form runs, so it is the safest tile to discard.",
  "coach.reason.lone-terminal": "{tile} is a terminal tile with no neighbours in your hand, so it has the fewest ways to become a set.",
  "coach.reason.isolated": "{tile} is isolated, so it currently has fewer ways to form a set.",
  "coach.reason.run-gap": "{tile} only has a neighbour two ranks away ({runs}), so it is the weakest tile left in your hand.",
  "coach.reason.run-gap.plain": "{tile} only has a neighbour two ranks away, so it is the weakest tile left in your hand.",
  "coach.reason.run-part": "{tile} is still connected, but the rest of your hand develops faster without it.",
  "coach.reason.pair": "{tile} is a pair, but discarding it costs you the least in this position.",
  "coach.reason.default": "{tile} is the least useful tile for your hand right now.",
  "coach.keep.triplet": "Keeping the three {tile} is worth it because that is already a complete set.",
  "coach.keep.pair": "Keeping the {tile} pair is worth it because it can become your final pair or grow into a pong.",
  "coach.keep.run": "Keeping {tile} and {others} is more useful because they can develop into several sequences ({runs}).",
  "coach.keep.run.plain": "Keeping {tile} and {others} is more useful because they can still form a run.",
  "coach.note.ready": "After this discard you are ready to win, waiting only for {tiles}.",
  "coach.steps.complete": "your hand is already complete",
  "coach.steps.one": "you are 1 tile away from winning",
  "coach.steps.many": "you need about {count} more tiles",
  "coach.situation.ended": "The round is over.",
  "coach.situation.claimWaiting": "Waiting for a claim decision.",
  "coach.situation.claim": "{player} discarded {tile}. That discard can be claimed.",
  "coach.situation.otherTurn": "It is {player}'s turn now.",
  "coach.situation.yourDraw": "Your turn: draw a tile from the wall.",
  "coach.situation.yourDiscard": "Your turn: discard one tile.",

  // ---------- game log ----------
  "log.title": "Game log",
  "log.empty": "No moves yet.",
  "log.drew.you": "{player} drew {tile}. Wall left: {wall}.",
  "log.drew.you.replacement": "{player} drew {tile} as a Kong replacement. Wall left: {wall}.",
  "log.drew.other": "{player} drew 1 tile.",
  "log.drew.other.replacement": "{player} drew 1 tile as a Kong replacement.",
  "log.discarded": "{player} discarded {tile}.",
  "log.claimWindow": "{tile} can be claimed.",
  "log.claimDeclared": "{player} declared {claim}.",
  "log.passed": "{player} passed.",
  "log.meldFormed": "{player} formed a {meld} with {tile}.",
  "log.meldFormed.concealed": "{player} formed a {meld} from their own hand.",
  "log.won.selfDraw": "{player} won on their own draw: {tile}!",
  "log.won.discard": "{player} won on the discard from {from}: {tile}!",
  "log.exhaustiveDraw": "The wall ran out. The round is a draw.",

  // ---------- end of round ----------
  "end.dialog": "Round finished",
  "end.win.you": "You win!",
  "end.win.other": "Round finished",
  "end.draw.title": "Draw",
  "end.draw.body": "The wall ran out before anyone won. There is no winner this round.",
  "end.win.selfDraw": "{player} won with {tile} (self-draw).",
  "end.win.fromDiscard": "{player} won with {tile} (from the discard by {from}).",
  "end.noScore": "This version does not count points yet: no regional scoring system has been chosen.",
  "end.restart": "Replay",
  "end.newGame": "New game",

  // ---------- help ----------
  "help.title": "How to play (short)",
  "help.step1": "On your turn: press Draw tile, then click one tile to discard it.",
  "help.step2": "You win when your hand becomes 4 sets + 1 pair.",
  "help.step3": "When another player discards you may call Chow (a run, only from the player on your left), Pong (three of a kind), Kong (four of a kind) or Win.",
  "help.step4": "If the wall runs out first, the round ends in a draw.",
  "help.glossaryTitle": "Glossary",

  // ---------- solo ----------
  "solo.title": "Solo",
  "solo.subtitle": "You against 3 deterministic bots. The same seed always deals the same tiles.",
  "solo.seed": "Seed",
  "solo.seedAria": "Game seed",
  "solo.playSeed": "Play this seed",
  "solo.newGame": "New game",
  "solo.replay": "Replay",
  "solo.replaySeed": "Replay this seed",
  "solo.footer": "Rule set: {ruleSet} · seed: {seed}",

  // ---------- tutorial ----------
  "tutorial.title": "Learn Mahjong",
  "tutorial.subtitle": "Seven short lessons. You really play: the coach explains every step.",
  "tutorial.restart": "Restart the tutorial",
  "tutorial.nav": "Lesson list",
  "tutorial.progress": "Lesson {current} of {total}",
  "tutorial.objective": "What you learn",
  "tutorial.task": "Task",
  "tutorial.coachSays": "The coach says",
  "tutorial.next": "Next: {title}",
  "tutorial.nextLesson": "Next lesson",
  "tutorial.replay": "Replay lesson",
  "tutorial.back": "Back to lessons",
  "tutorial.allDone": "All lessons finished. Now try Solo mode!",
  "tutorial.keepPlaying": "You may keep playing at this table; the lesson does not stop the game.",
  "tutorial.overview.title": "Seven lessons",
  "tutorial.overview.subtitle": "Pick a lesson. You can replay any of them at any time.",
  "tutorial.start": "Start lesson",
  "tutorial.lessonNumber": "Lesson {number}",

  // ---------- online ----------
  "online.title": "Online",
  "online.subtitle": "The authoritative server runs the same rules. The browser never decides the outcome.",
  "online.status.idle": "Not connected",
  "online.status.connecting": "Connecting...",
  "online.status.reconnecting": "Reconnecting...",
  "online.status.online": "Connected",
  "online.status.offline": "Disconnected",
  "online.status.error": "Connection failed",
  "online.lobby.title": "Create or join a room",
  "online.name": "Name",
  "online.nameAria": "Player name",
  "online.defaultName": "Player",
  "online.createRoom": "Create room",
  "online.reconnect": "Reconnect",
  "online.roomCode": "Room code",
  "online.join": "Join",
  "online.lobbyHint": "Share the room code with friends. When the host starts, empty seats are filled with bots by the server.",
  "online.roomTitle": "Room",
  "online.seatList": "Seats in the room",
  "online.addBot": "Add bot",
  "online.start": "Start game",
  "online.leave": "Leave",
  "online.seat": "Seat {number}",
  "online.seat.empty": "empty",
  "online.seat.you": "(You)",
  "online.badge.host": "host",
  "online.badge.disconnected": "disconnected",
  "online.waitingHost": "Waiting for the host to start. Empty seats are filled with bots run by the server.",
  "online.roomExtra": "Room {code}",
  "online.newRound": "New round",
  "online.leaveRoom": "Leave room",
  "online.notConnected": "Not connected to the server.",
  "online.badMessage": "Could not read the message from the server.",
  "online.connectFailed": "Could not connect to the game server.",
  "online.reconnectFailed": "The connection still failed after several attempts. Try reconnecting.",
  "online.rejected": "Action rejected: {reason}",
  "online.serverError": "Server refused: {reason}",
  "notice.close": "close",

  // ---------- table surface (milestone 3b) ----------
  // ---------- M6 game shell ----------
  "shell.menu": "Menu",
  "shell.menu.open": "Open game menu",
  "shell.menu.close": "Close menu",
  "shell.menu.title": "Game menu",
  "shell.section.settings": "Settings",
  "shell.stage": "Game table",
  "shell.coach.open": "Ask the coach",

  "table.region": "Mahjong table",
  "table.center": "Center board",
  "table.compass": "Seat winds at the table",
  "table.actions": "Your actions",
  "table.yourDiscards": "Your discard area",
  "table.latestDiscard": "latest discard",
  "seat.dealerBadge": "dealer",
  "hand.rack": "Your tile rack",
  "hand.drawn": "Newly drawn tile",
  "tutorial.details": "Full explanation",

  // ---------- opening & play preferences ----------
  "sound.preference": "Sound",
  "opening.preference": "Opening animation",
  "opening.full": "Full",
  "opening.fast": "Fast",
  "opening.off": "Off",
  "opening.skip": "Skip",
  "opening.title": "Mahjong opening ceremony",
  "opening.stage.settle": "Set the table",
  "opening.stage.shuffle": "Shuffle tiles",
  "opening.stage.wall": "Build the wall",
  "opening.stage.dice": "Roll the dice",
  "opening.stage.deal": "Deal tiles",
  "opening.stage.ready": "Ready to play",
  "opening.detail.settle": "Four seats, one table, 136 tiles.",
  "opening.detail.shuffle": "136 face-down tiles are churned in the middle.",
  "opening.detail.wall": "Four walls, each with 17 stacks × 2 tiles.",
  "opening.detail.dice": "The dice land on {a} and {b} - ceremony only.",
  "opening.detail.deal": "Tile backs are dealt toward all four seats.",
  "opening.detail.ready": "The table is ready.",
  "opening.readyDetail": "Dealer: {dealer}. Turn: {player}.",

  // ---------- portal: shell ----------
  "portal.documentTitle": "kikik.id - play and learn",
  "portal.skipToContent": "Skip to main content",
  "portal.nav.label": "kikik.id navigation",
  "portal.nav.home": "Home",
  "portal.nav.games": "Games",
  "portal.nav.categories": "Categories",
  "portal.nav.learn": "Learn",
  "portal.brand.aria": "kikik.id, back to home",
  "portal.search.open": "Search",
  "portal.search.label": "Search games, categories and moods",
  "portal.search.placeholder": "Search games or categories",
  "portal.search.close": "Close search",
  "portal.search.results": "{count} results for “{query}”",
  "portal.search.empty": "Nothing matches “{query}”. kikik has one game so far.",
  "portal.search.kind.game": "Game",
  "portal.search.kind.category": "Category",
  "portal.search.kind.mood": "Mood",
  "portal.search.hint": "Press Enter to open the first result.",

  // ---------- portal: status, honestly stated ----------
  "portal.status.playable": "Playable now",
  "portal.status.comingSoon": "Coming soon",
  "portal.stamp.play": "PLAY",
  "portal.stamp.soon": "SOON",
  "portal.badge.oneGame": "1 game",
  "portal.badge.noGame": "No games yet",
  "portal.platform.label": "Playable on",
  "portal.platform.web": "Browser",
  "portal.platform.android": "Android",

  // ---------- portal: hero ----------
  "portal.hero.eyebrow": "Our first title",
  "portal.hero.titleA": "Never played Mahjong?",
  "portal.hero.titleB": "Start here.",
  "portal.hero.body": "Four-player Mahjong, explained from zero. Seven lessons, a coach that talks through every move, then a real table against three bots.",
  "portal.hero.play": "Play now",
  "portal.hero.how": "How to play",
  "portal.hero.note": "Free, no account, straight in the browser.",
  "portal.hero.artLabel": "Seven Mahjong tiles standing on the kikik table",
  "portal.ladder.label": "How learning works on kikik",
  "portal.ladder.learn.name": "Learn",
  "portal.ladder.learn.desc": "7 lessons + coach",
  "portal.ladder.solo.name": "Solo",
  "portal.ladder.solo.desc": "You against 3 bots",
  "portal.ladder.online.name": "Online",
  "portal.ladder.online.desc": "One table for four · still in testing",

  // ---------- portal: pick a mood ----------
  "portal.intent.eyebrow": "Start from a mood",
  "portal.intent.title": "What do you feel like playing?",
  "portal.intent.lead": "Pick one. A game is only tagged when it genuinely fits, so some moods are still empty.",
  "portal.intent.empty": "No games for this mood yet. kikik has one title so far.",
  "portal.intent.showing": "Mood selected: {mood}",
  "portal.intent.clear": "Show everything again",
  "portal.mood.brain-boost": "Brain Boost",
  "portal.mood.play-together": "Play Together",
  "portal.mood.quick-play": "Quick Play",
  "portal.mood.relax": "Relax",
  "portal.mood.challenge": "Challenge",
  "portal.mood.learn-through-play": "Learn Through Play",
  "portal.mood.timeless-classics": "Timeless Favorites",
  "portal.mood.new-on-kikik": "New on Kikik",

  // ---------- portal: kikik picks ----------
  "portal.picks.eyebrow": "kikik picks",
  "portal.picks.title": "One game, made properly.",
  "portal.picks.why.title": "Why Mahjong came first",
  "portal.picks.why.body": "Mahjong is easy to grasp, hard to master, and almost never taught to Indonesian beginners in their own language. So that is where we started.",
  "portal.picks.fact1.title": "136 tiles, classical rules",
  "portal.picks.fact1.body": "No flowers, no jokers. A regional scoring system is deliberately not chosen yet.",
  "portal.picks.fact2.title": "Seven lessons, in order",
  "portal.picks.fact2.body": "From drawing your first tile to winning off an opponent's discard.",
  "portal.picks.fact3.title": "Bots you can predict",
  "portal.picks.fact3.body": "The same seed deals the same tiles, so a round can be replayed exactly.",
  "portal.picks.cta": "See Kikik Mahjong",

  // ---------- portal: category shelves ----------
  "portal.worlds.eyebrow": "Game shelves",
  "portal.worlds.title": "Twelve shelves. One is stocked.",
  "portal.worlds.lead": "We show the empty shelves too, so it is clear where kikik is growing.",
  "portal.category.board-strategy.name": "Board & Strategy",
  "portal.category.board-strategy.blurb": "Boards, tiles, and plans that take time.",
  "portal.category.puzzle-brain.name": "Puzzle & Brain",
  "portal.category.puzzle-brain.blurb": "Patterns, logic, and the “of course” moment.",
  "portal.category.cards-classics.name": "Cards & Classics",
  "portal.category.cards-classics.blurb": "A deck of cards and rules that already work.",
  "portal.category.arcade-casual.name": "Arcade & Casual",
  "portal.category.arcade-casual.blurb": "Five minutes, one round, straight in.",
  "portal.category.action.name": "Action",
  "portal.category.action.blurb": "Fast reflexes and pressure that keeps climbing.",
  "portal.category.adventure.name": "Adventure",
  "portal.category.adventure.blurb": "Walk, find something, then go further.",
  "portal.category.sports-racing.name": "Sports & Racing",
  "portal.category.sports-racing.blurb": "A start line, a finish line, a time to beat.",
  "portal.category.simulation.name": "Simulation",
  "portal.category.simulation.blurb": "Set it up, run it, watch what happens.",
  "portal.category.multiplayer-party.name": "Multiplayer & Party",
  "portal.category.multiplayer-party.blurb": "One room, several people, plenty of noise.",
  "portal.category.music-rhythm.name": "Music & Rhythm",
  "portal.category.music-rhythm.blurb": "A beat you have to keep up with.",
  "portal.category.family.name": "Family",
  "portal.category.family.blurb": "Easy enough to play together.",
  "portal.category.learn-explore.name": "Learn & Explore",
  "portal.category.learn-explore.blurb": "Play first; notice you learned something later.",

  // ---------- portal: learn through play ----------
  "portal.learn.eyebrow": "Learn through play",
  "portal.learn.title": "Learn without it feeling like homework.",
  "portal.learn.body": "kikik is built for people who have never tried before. Rules are explained when you need them, at a real table, not on a long page of theory.",
  "portal.learn.now.title": "What you can try today",
  "portal.learn.now.body": "Learn mode in Kikik Mahjong: seven lessons in order, a table set up for one decision, and a coach that explains the reason behind it.",
  "portal.learn.now.cta": "Start at lesson 1",
  "portal.learn.next.title": "Directions we are preparing",
  "portal.learn.honest": "Not one education title has shipped yet. This is a direction, not a promise.",
  "portal.learn.topic.science": "Science",
  "portal.learn.topic.logic": "Logic",
  "portal.learn.topic.math": "Mathematics",
  "portal.learn.topic.language": "Languages",
  "portal.learn.topic.tech": "Technology",
  "portal.learn.topic.simulation": "Simulation",
  "portal.learn.topic.experiment": "Experiments",

  // ---------- portal: coming next ----------
  "portal.next.eyebrow": "Next",
  "portal.next.title": "Where kikik grows",
  "portal.next.lead": "No dates and no promises. This is the order that makes sense to us.",
  "portal.next.item1.title": "Mahjong on Android",
  "portal.next.item1.body": "Same rules, same engine, a touchscreen.",
  "portal.next.item2.title": "Online tables for everyone",
  "portal.next.item2.body": "The server runs for development, not yet for the public.",
  "portal.next.item3.title": "A second shelf",
  "portal.next.item3.body": "Cards & Classics or Puzzle & Brain first.",
  "portal.next.item4.title": "A first education title",
  "portal.next.item4.body": "Built on the Learn mode that already exists.",

  // ---------- portal: footer ----------
  "portal.footer.blurb": "A place to play and learn, in Indonesian first.",
  "portal.footer.honest": "One game has shipped. The rest do not exist yet, and we do not pretend otherwise.",
  "portal.footer.madeIn": "Made in Indonesia",

  // ---------- portal: game page ----------
  "portal.game.mahjong.title": "Kikik Mahjong",
  "portal.game.mahjong.tagline": "Mahjong for beginners, in plain language",
  "portal.game.mahjong.description": "Classical four-player Mahjong: 136 tiles, no flowers, no jokers. Learn it from zero, play solo against three bots, then sit at one table with other people.",
  "portal.entry.eyebrow": "Board & Strategy · Playable now",
  "portal.entry.back": "kikik.id home",
  "portal.entry.homeAria": "Back to the kikik.id home page",
  "portal.entry.choose": "Choose how to play",
  "portal.entry.chooseLead": "All three run on the same engine and the same rules. If this is your first time, start with Learn.",
  "portal.entry.open": "Open",
  "portal.entry.about.title": "What is in the game",
  "portal.entry.rules.title": "The rules it uses",
  "portal.entry.rules.body": "Four players, 136 tiles, chow / pong / kong, and a win on 4 sets + 1 pair. A regional scoring system is deliberately not chosen yet.",
  "portal.exit.label": "kikik.id",
  "portal.exit.aria": "Leave the table and go back to the kikik.id home page",

  // ---------- errors (engine + protocol codes) ----------
  "error.GAME_ENDED": "The round is already over. Start a new game.",
  "error.NOT_YOUR_TURN": "It is not your turn yet.",
  "error.WRONG_PHASE": "That move is not possible right now.",
  "error.WALL_EMPTY": "The wall is empty.",
  "error.TILE_NOT_IN_HAND": "That tile is not in your hand.",
  "error.NOT_A_WINNING_HAND": "Your hand is not complete yet: it needs 4 sets + 1 pair.",
  "error.CLAIM_NOT_SUPPORTED": "That claim is not available in this rule set.",
  "error.NOT_IN_CLAIM_WINDOW": "There is no claim waiting for your answer.",
  "error.ILLEGAL_CLAIM": "That claim is not legal for this tile.",
  "error.UNKNOWN_PLAYER": "Unknown player.",
  "error.UNKNOWN_ACTION": "Unknown action.",
  "error.BAD_MESSAGE": "The server did not recognise that message.",
  "error.UNSUPPORTED_PROTOCOL": "Protocol version mismatch. Reload the page.",
  "error.NO_SESSION": "Session not found. Reconnect.",
  "error.ROOM_NOT_FOUND": "There is no room with that code.",
  "error.ROOM_FULL": "That room is already full.",
  "error.ALREADY_IN_ROOM": "You are already in a room.",
  "error.NOT_IN_ROOM": "You are not in a room.",
  "error.NOT_HOST": "Only the host can do that.",
  "error.GAME_ALREADY_STARTED": "The game has already started.",
  "error.GAME_NOT_STARTED": "The game has not started yet.",
  "error.NOT_YOUR_SEAT": "That is not your seat.",
  "error.RESUME_FAILED": "Could not rejoin the previous room.",
};
